import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useSelector } from 'react-redux'
import numeral from 'numeral'
import moment from 'moment'
import cn from 'classnames'

import Table from '../../../Invoices/Table'

import styles from './styles.module.scss'

import { getCurrency } from '../../../../helpers/currencies'

const columnHelper = createColumnHelper()

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const getDuration = (minutes) => {
  const duartion = moment.duration(minutes, 'minutes')
  const hours = Math.floor(duartion.asHours())
  const mins = duartion.minutes()
  return `${hours}h ${mins}m`
}

const EmployeeProfitability = ({sales, employees}) => {
  const { t } = useTranslation()
  
  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)

  const data = useMemo(() => {
    const salesByemployee = sales.reduce((acc, sale) => {
      return {
        ...acc,
        [sale['WaiterName.ID']]: acc[sale['WaiterName.ID']] ? [...acc[sale['WaiterName.ID']], sale] : [sale],
      }
    }, {})
    return employees.map((emp => {
      const sales = salesByemployee[emp.iiko_user_id] ? salesByemployee[emp.iiko_user_id].reduce((acc, sale) => acc + (sale.DishDiscountSumInt || 0), 0) : 0
      return {
        ...emp,
        sales: sales,
        rawMaterialCost: salesByemployee[emp.iiko_user_id] ? salesByemployee[emp.iiko_user_id].reduce((acc, sale) => acc + (sale['ProductCostBase.ProductCost'] || 0), 0) : 0,
        salesPerHour: emp.work_duration > 0 ? sales / (emp.work_duration / 60) : 0,
        // bills: salesByemployee[emp.iiko_user_id] ? getUniqueArrayByKey(salesByemployee[emp.iiko_user_id], 'OrderNum').length : 0,
      }
    }))
  }, [sales, employees])

  const columns = useMemo(() => ([
    columnHelper.accessor('name', {
      id: 'name',
      header: t('Employee'),
      enableSorting: false,
      cell: ({ getValue, row }) => {
        return `${getValue()} ${row.original.surname}`
      }
    }),
    columnHelper.accessor('skill', {
      id: 'skill',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
      header: t('Role'),
    }),
    columnHelper.accessor('work_duration', {
      id: 'work_duration',
      enableSorting: false,
      cell: ({ getValue }) => getDuration(getValue()),
      header: t('Worked hours'),
    }),
    columnHelper.accessor('sales', {
      id: 'sales',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Sales'),
    }),
    columnHelper.accessor('rawMaterialCost', {
      id: 'rawMaterialCost',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Material cost'),
    }),
    columnHelper.display({
      id: 'laborCost',
      enableSorting: false,
      cell: ({row}) => {
        const employeeCost = (row.original.profitability?.cost || 0) * Number(row.original.em_status || 1) * (row.original.work_duration / 60)
        return formatNumber(employeeCost, currency.symbol)
      },
      header: t('Labor cost'),
    }),
    columnHelper.display({
      id: 'profit',
      enableSorting: false,
      cell: ({row}) => {
        const sales = row.original.sales || 0
        const rawMaterialCost = row.original.rawMaterialCost || 0
        const employeeCost = row.original.profitability?.cost || 0
        const profit = sales - rawMaterialCost - employeeCost
        return (
          <span className={cn({[styles.negative]: profit < 0, [styles.positive]: profit > 0})}>{ formatNumber(profit, currency.symbol) }</span>
        )
      },
      header: t('Profit'),
    }),
    columnHelper.display({
      id: 'profitPct',
      enableSorting: false,
      cell: ({row}) => {
        const sales = row.original.sales || 0
        const rawMaterialCost = row.original.rawMaterialCost || 0
        const employeeCost = row.original.profitability?.cost || 0
        const profit = sales - rawMaterialCost - employeeCost
        const percent = sales > 0 ? (profit / sales * 100) : 0
        return (
          <span className={cn({[styles.negative]: percent < 0, [styles.positive]: percent > 0})}>{`${numeral(percent).format('0.[00]')}%`}</span>
        )
      },
      header: t('Profit %'),
    }),
    columnHelper.accessor('salesPerHour', {
      id: 'salesPerHour',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Sales per hour'),
    }),
    
  ]), [t, currency.symbol])

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('Employee profitability')}</div>
      </div>
      <Table
        table={table}
        columns={columns}
        wrapperStyle={{flex: 1}}
        headerClassName={styles.tableHeader} />
    </div>
  )
}

export default EmployeeProfitability
